import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Footer } from '../../components/Footer/Footer';
import { StepProps } from '../../components/OrionRiskToleranceWorkflow/OrionRiskToleranceWorkflow.meta';
import { StepId } from '../../api/useRiskToleranceWorkflowQuery';
import { Content } from '../../components/Content/Content';
import { StepCard } from '../../components/StepCard/StepCard';
import { VisualPicker } from '../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { ariaLabel, FormValues, options } from './HypotheticalPortfolio.meta';
import { PortfolioReturns } from './components/PortfolioReturns/PortfolioReturns';

const Title = () => {
  return (
    <>
      <Text as="h1" fontSize="h5" fontWeight={500} mb="32">
        Assume you are investing <span style={{ fontWeight: 600 }}>$100,000</span> and can choose
        from the five hypothetical portfolios shown below. The values for each portfolio represent
        two possible returns (low and high) after one year.
      </Text>

      <Text as="p" fontSize="h5" fontWeight={500} mb="32">
        If there is an equal chance of achieving either result (low or high), which portfolio
        represents the maximum risk/return trade-off you’d be willing to accept?
      </Text>
    </>
  );
};

export const HypotheticalPortfolio: React.FC<StepProps> = ({
  answers,
  onNext,
  onPrevious,
  onSubmit,
  progress,
}) => {
  const { handleSubmit, setValue, control } = useForm<FormValues>({
    defaultValues: {
      [StepId.HypotheticalPortfolio]: answers.hypotheticalPortfolio,
    },
  });

  const onSubmitStep = ({ hypotheticalPortfolio }: FormValues) => {
    onSubmit({ stepId: StepId.HypotheticalPortfolio, answer: hypotheticalPortfolio });
    onNext();
  };

  return (
    <Box minWidth={864}>
      <form onSubmit={handleSubmit(onSubmitStep)}>
        <Content>
          <StepCard title={<Title />} textAlign="left">
            <Box display="flex" mb="16">
              <Box flex={1} />

              <Box flex={1}>
                <Text variant="smallCaps" textAlign="right" mr="16">
                  High Value
                </Text>
              </Box>

              <Box flex={1}>
                <Text variant="smallCaps" textAlign="right" mr="16">
                  Low Value
                </Text>
              </Box>
            </Box>

            <Controller
              control={control}
              name={StepId.HypotheticalPortfolio}
              render={({ field: { value: formValue } }) => (
                <Box
                  display="flex"
                  flexDirection="column"
                  gridGap="16"
                  role="radiogroup"
                  aria-label={ariaLabel}
                >
                  {options.map(({ name, value, worstCase, bestCase }) => {
                    const checked = value === formValue;

                    return (
                      <Box key={value} position="relative">
                        <VisualPicker
                          label={name}
                          onChange={() => setValue(StepId.HypotheticalPortfolio, value)}
                          checked={checked}
                        />

                        <PortfolioReturns returns={[bestCase, worstCase]} checked={checked} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            />

            <Text fontSize="small" color="neutral.500" mt="16">
              The maximum gain or loss on an investment is impossible to predict. The ranges shown
              are hypothetical and are designed solely to gauge an investor’s tolerance for risk.
            </Text>
          </StepCard>
        </Content>

        <Footer onPrevious={onPrevious} progress={progress} />
      </form>
    </Box>
  );
};
